import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "taxId",
    "legalBusinessName",
    "streetNo",
    "streetAddress",
    "city",
    "state",
    "zip",
    "businessType",
    "phone",
    "indicator"
  ]

  static values = {
    decodeUrl: String
  }

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  // Called when tax_id field changes
  onTaxIdInput() {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Hide indicator while typing
    this.hideIndicator()

    // Debounce API calls (wait 500ms after user stops typing)
    this.debounceTimer = setTimeout(() => {
      this.attemptDecode()
    }, 500)
  }

  async attemptDecode() {
    const ein = this.taxIdTarget.value.trim()

    // Check if EIN matches the test pattern (99-9XSSCCC or 999XSSCCC)
    const einPattern = /^99-?9\d{6}$/
    if (!einPattern.test(ein)) {
      return // Silently ignore if not a test EIN
    }

    try {
      const response = await fetch(this.decodeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ ein: ein })
      })

      if (response.ok) {
        const data = await response.json()
        this.populateFields(data)
        this.showSuccessIndicator()
      } else {
        // Silently fail - don't show errors for invalid EINs
        this.hideIndicator()
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience on network errors
      console.debug("EIN decode failed:", error)
      this.hideIndicator()
    }
  }

  populateFields(data) {
    const preview = data.business_preview

    // Only populate empty fields (don't overwrite user data)
    if (!this.legalBusinessNameTarget.value && preview.business_name) {
      this.legalBusinessNameTarget.value = preview.business_name
      this.highlightField(this.legalBusinessNameTarget)
    }

    // Parse address (e.g., "1000 Main Street") into street_no and street_address
    if (preview.address) {
      const addressMatch = preview.address.match(/^(\d+)\s+(.+)$/)
      if (addressMatch) {
        const [, streetNo, streetName] = addressMatch

        if (!this.streetNoTarget.value) {
          this.streetNoTarget.value = streetNo
          this.highlightField(this.streetNoTarget)
        }

        if (!this.streetAddressTarget.value) {
          this.streetAddressTarget.value = streetName
          this.highlightField(this.streetAddressTarget)
        }
      } else if (!this.streetAddressTarget.value) {
        // If we can't parse it, just put everything in street_address
        this.streetAddressTarget.value = preview.address
        this.highlightField(this.streetAddressTarget)
      }
    }

    if (!this.cityTarget.value && preview.city) {
      this.cityTarget.value = preview.city
      this.highlightField(this.cityTarget)
    }

    if (!this.stateTarget.value && preview.state) {
      this.stateTarget.value = preview.state
      this.highlightField(this.stateTarget)
    }

    if (!this.zipTarget.value && preview.zip) {
      this.zipTarget.value = preview.zip
      this.highlightField(this.zipTarget)
    }

    if (!this.businessTypeTarget.value && data.industry) {
      this.businessTypeTarget.value = data.industry
      this.highlightField(this.businessTypeTarget)
    }

    if (!this.phoneTarget.value && preview.phone) {
      this.phoneTarget.value = preview.phone
      this.highlightField(this.phoneTarget)
    }
  }

  highlightField(field) {
    // Add a temporary highlight animation
    field.classList.add("bg-emerald-50", "border-emerald-400")
    setTimeout(() => {
      field.classList.remove("bg-emerald-50", "border-emerald-400")
    }, 2000)
  }

  showSuccessIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.hidden = false
      this.indicatorTarget.innerHTML = `
        <span class="inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
          <svg class="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          Auto-filled
        </span>
      `
    }
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.hidden = true
      this.indicatorTarget.innerHTML = ""
    }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta && meta.getAttribute("content")
  }
}
